// @flow

import React, {useState, useCallback, useEffect, useRef, useMemo} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {
  CircularProgress,
  Divider,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import CustomModal from '../../../../elements/CustomModal/CustomModal';
import {colors} from '../../../../assets/styles/colors';
import FilterButton from '../Sections/FilterButton/FilterButton';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {GetApp} from '@material-ui/icons';
import SearchBar from '../../../../elements/SearchBar/SearchBar';
import {commaFormat} from '../../../../utils/number';
import RowItem from '../Sections/RowItem/RowItemSales';

import {endpoints} from '../../api';
import type {Currency, Pagination} from '../../../../entities';
import type {DashboardAction, PromotionSalesData} from '../../duck';

import type {RootState} from '../../../../modules';

export default connect(
  (state: RootState) => ({
    paginatedPromotions: state.dashboard.paginatedPromotions,
    promotionsPagination: state.dashboard.promotionsPagination,
    isFetchingPromotionsPaginated:
      state.dashboard.isFetchingPromotionsPaginated,
  }),
  null
)(PromotionsSalesModal);

const useStyles = makeStyles(theme => ({
  customModal: {
    '& .MuiPaper-root': {
      padding: '16px!important',
      maxHeight: 580,
    },
  },
  root: {
    width: 520,
    maxWidth: '100%',
  },
  title: {
    color: colors.grey900,
    fontWeight: 600,
    fontSize: 18,
    textAlign: 'center',
  },
  divider: {
    backgroundColor: '#E8E8ED',
    margin: theme.spacing(2, 0),
  },
  groupFilter: {
    display: 'flex',
    alignItems: 'center',
  },
  wrapFilter: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-between',
  },
  wrapItems: {
    maxHeight: '400px',
    overflowX: 'hidden',
    overflowY: 'auto',
    margin: theme.spacing(2, 0),
    minHeight: '200px',
    [theme.breakpoints.up('md')]: {
      // minHeight: 'unset',
      maxHeight: '300px',
    },
  },
  advice: {
    fontSize: 14,
    color: colors.grey700,
  },
  rowItem: {
    margin: 0,
  },
}));

type Props = {
  paginatedPromotions?: PromotionSalesData[],
  currency?: Currency,
  open: boolean,
  setOpen: (v: boolean) => void,
  promotionsPagination?: Pagination,
  fetchPromotionSales: (payload: DashboardAction) => void,
  isFetchingPromotionsPaginated?: boolean,
};
function PromotionsSalesModal(props: Props) {
  const {
    paginatedPromotions,
    currency,
    open,
    setOpen,
    fetchPromotionSales,
    promotionsPagination,
    isFetchingPromotionsPaginated,
  } = props;

  const [filter, setFilter] = useState('promotion');
  const classes = useStyles();

  const handleUpdateFilter = (value: string) => {
    setFilter(value);
  };

  const buildQueryParamsCallback = useCallback(
    (searchString?: string, reset: boolean = true) => {
      let params: {[key: string]: any} = {
        paginated: true,
        reset: !!reset,
      };
      if (searchString) {
        params[filter] = searchString;
      }
      if (filter) {
        params.type = filter;
      }
      return params;
    },
    [filter]
  );

  const fetchPromotionsCallback = useCallback(
    (searchString?: string, reset?: boolean) => {
      const queryFilter = buildQueryParamsCallback(searchString, reset);
      if (queryFilter) {
        fetchPromotionSales({queryFilter});
      }
    },
    [buildQueryParamsCallback, fetchPromotionSales]
  );

  const handleAddPage = () => {
    if (
      !isFetchingPromotionsPaginated &&
      promotionsPagination &&
      promotionsPagination.total > promotionsPagination.size &&
      paginatedPromotions &&
      paginatedPromotions.length < promotionsPagination.total
    ) {
      fetchPromotionsCallback(undefined, false);
    }
  };

  const promotionItemsRef = useRef();
  const onScroll = () => {
    if (promotionItemsRef.current) {
      const {scrollTop, scrollHeight, clientHeight} = promotionItemsRef.current;
      if (scrollTop + clientHeight === scrollHeight) {
        handleAddPage();
      }
    }
  };

  const fileDownloadUrl = useMemo(() => {
    const endpoint =
      filter === 'promotion'
        ? endpoints.GET_PROMOTION_SALES
        : endpoints.GET_COUPON_SALES;
    return `${endpoint}?mode=xlsx`;
  }, [filter]);

  useEffect(() => {
    if (open) {
      fetchPromotionsCallback();
    }
  }, [fetchPromotionsCallback, open]);

  return (
    <CustomModal
      open={open}
      handleCloseModal={() => setOpen(false)}
      className={classes.customModal}
    >
      <div className={classes.root}>
        <Typography className={classes.title}>Ventas por promoción</Typography>
        <Divider className={classes.divider} />
        <div className={classes.wrapFilter}>
          <div className={classes.groupFilter}>
            <FilterButton
              id="promotion"
              text="Promociones"
              selected={filter === 'promotion'}
              handleOnClick={handleUpdateFilter}
            />
            <FilterButton
              id="coupon"
              text="Cupones"
              selected={filter === 'coupon'}
              handleOnClick={handleUpdateFilter}
            />
          </div>
          <CustomButton
            startIcon={<GetApp />}
            href={fileDownloadUrl}
            className={classes.btnDownload}
            variant="contained"
            target="_blank"
            rel="noopener noreferrer"
          >
            Descargar
          </CustomButton>
        </div>
        <SearchBar
          mobile={true}
          placeholder="Buscar promoción o cupón"
          color="light"
          externalHandleSearch={fetchPromotionsCallback}
        />
        <div
          className={classes.wrapItems}
          onScroll={onScroll}
          ref={promotionItemsRef}
        >
          <Grid container spacing={2}>
            {paginatedPromotions && paginatedPromotions.length ? (
              paginatedPromotions.map((p, idx) => (
                <Grid item xs={12} key={p.name || p.code}>
                  <RowItem
                    className={classes.rowItem}
                    id={idx + 1}
                    text={filter.includes('promotion') ? p.name : p.code || ''}
                    price={commaFormat(
                      p.amount ? p.amount : 0,
                      currency ? currency.symbol : ''
                    )}
                  />
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography className={classes.advice}>
                  No existen ventas.
                </Typography>
              </Grid>
            )}
            {isFetchingPromotionsPaginated && (
              <Grid item xs={12}>
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress color="primary" />
                </div>
              </Grid>
            )}
          </Grid>
        </div>
      </div>
    </CustomModal>
  );
}

PromotionsSalesModal.propTypes = {
  currency: PropTypes.object,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  fetchPromotionSales: PropTypes.func.isRequired,
  promotionsPagination: PropTypes.object,
  paginatedPromotions: PropTypes.array,
  isFetchingPromotionsPaginated: PropTypes.bool,
};
